/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import * as _ from 'lodash';

import {OrderApiService} from '../order/order-api.service';
import {
  advertiserRefFromRelated,
  advertiserRefFromWarning,
  asAdvertiserRef,
  creativeIdsFromWarning,
  findCreativeAdvertiserMismatch,
  findFolioCreativeAdvertisersMismatch,
  findOrderCreativeAdvertisersMismatch,
  isFolioAdvertiserMismatchWarning,
  isOrderAdvertiserMismatchWarning
} from './creative-advertiser-mismatch';
import {CreativeSetInfoApiService} from '../creative-set/creative-set-info-api.service';
import {FolioApiService} from '../folio/folio-api.service';
import {CreativeApiService} from './creative-api.service';
import {LibraryCreativeApiService} from '../library-creative/library-creative-api.service';
import {LineItemApiService} from '../line-item/line-item-api.service';

@Injectable({providedIn: 'root'})
export class CreativeAdvertiserMismatchService {
  constructor(
    private readonly creativeApi: CreativeApiService,
    private readonly libraryCreativeApi: LibraryCreativeApiService,
    private readonly lineItemApi: LineItemApiService,
    private readonly orderApi: OrderApiService,
    private readonly creativeSetInfoApi: CreativeSetInfoApiService,
    private readonly folioApi: FolioApiService,
  ) {
  }

  findMismatch(warnings: any): any {
    return findCreativeAdvertiserMismatch(warnings);
  }

  updateAdvertiser(creative: any, options?: {libraryCreative?: boolean}): Promise<any> {
    if (!creative?.id) {
      return Promise.reject(new Error('Creative id is required'));
    }
    const api = options?.libraryCreative ? this.libraryCreativeApi : this.creativeApi;
    return this.resolveExpectedAdvertiser(creative, api).then((advertiser) => {
      if (!advertiser?.id) {
        return Promise.reject(new Error('Expected advertiser could not be resolved'));
      }
      return firstValueFrom(api.savePartial({
        id: creative.id,
        advertiser: {id: advertiser.id}
      }, ['advertiser']));
    }).then((saved: any) => {
      if (creative) {
        creative.advertiser = saved.advertiser;
        _.set(creative, 'validationWarnings', saved.validationWarnings);
      }
      return saved;
    });
  }

  updateOrderCreativesAdvertiser(order: any): Promise<any> {
    return this.updateParentCreativesAdvertiser(order, 'order');
  }

  updateFolioCreativesAdvertiser(folio: any): Promise<any> {
    return this.updateParentCreativesAdvertiser(folio, 'folio');
  }

  private updateParentCreativesAdvertiser(parent: any, kind: 'order' | 'folio'): Promise<any> {
    if (!parent?.id) {
      return Promise.reject(new Error(kind === 'folio' ? 'Folio id is required' : 'Order id is required'));
    }
    const knownAdvertiser = asAdvertiserRef(parent.advertiser);
    const advertiserPromise = knownAdvertiser
      ? Promise.resolve(knownAdvertiser)
      : kind === 'folio'
        ? firstValueFrom(this.folioApi.get(parent.id)).then((fresh: any) => asAdvertiserRef(fresh.advertiser))
        : firstValueFrom(this.orderApi.getDataView({id: parent.id})).then((fresh: any) => asAdvertiserRef(fresh.advertiser));
    return advertiserPromise.then((advertiser) => {
      if (!advertiser?.id) {
        return Promise.reject(new Error(kind === 'folio'
          ? 'Folio advertiser could not be resolved'
          : 'Order advertiser could not be resolved'));
      }
      return this.affectedCreativeIds(parent, kind).then((ids) => {
        if (!ids.length) {
          return this.refreshParentWarnings(parent, kind);
        }
        const payload = _.map(ids, (id: string) => {
          return {id: id, advertiser: {id: advertiser.id}};
        });
        return firstValueFrom(this.creativeApi.bulkSave(payload)).then(() => {
          return this.refreshParentWarnings(parent, kind);
        });
      });
    });
  }

  private affectedCreativeIds(parent: any, kind: 'order' | 'folio'): Promise<string[]> {
    const mismatch = kind === 'folio'
      ? findFolioCreativeAdvertisersMismatch(parent.validationWarnings)
      : findOrderCreativeAdvertisersMismatch(parent.validationWarnings);
    const fromWarning = creativeIdsFromWarning(mismatch);
    if (fromWarning.length) {
      return Promise.resolve(fromWarning);
    }
    const parentAdvertiserId = _.get(parent, ['advertiser', 'id']);
    const query = kind === 'folio'
      ? {folio: parent.id, pageSize: 1001}
      : {order: parent.id, pageSize: 1001};
    return firstValueFrom(this.creativeApi.listOptions(query)).then((page: any) => {
      return _.map(_.filter(page.results || [], (creative: any) => {
        if (findCreativeAdvertiserMismatch(creative.validationWarnings)) {
          return true;
        }
        const creativeAdvertiserId = _.get(creative, ['advertiser', 'id']);
        return !!parentAdvertiserId && !!creativeAdvertiserId && creativeAdvertiserId !== parentAdvertiserId;
      }), 'id');
    });
  }

  private refreshParentWarnings(parent: any, kind: 'order' | 'folio'): Promise<any> {
    return new Promise((resolve) => {
      setTimeout(resolve, 1000);
    }).then(() => {
      const refresh = kind === 'folio'
        ? firstValueFrom(this.folioApi.refresh(parent.id))
        : firstValueFrom(this.orderApi.refresh(parent.id));
      return refresh.then((fresh: any) => {
        _.set(parent, 'validationWarnings', fresh.validationWarnings);
        if (fresh.advertiser) {
          parent.advertiser = fresh.advertiser;
        }
        return fresh;
      });
    });
  }

  private resolveExpectedAdvertiser(creative: any, api: CreativeApiService | LibraryCreativeApiService): Promise<{id: string; name?: string} | null> {
    const warning = findCreativeAdvertiserMismatch(creative.validationWarnings);
    const fromWarning = advertiserRefFromWarning(warning);
    if (fromWarning) {
      return Promise.resolve(fromWarning);
    }
    const fromRelated = advertiserRefFromRelated(creative);
    if (fromRelated) {
      return Promise.resolve(fromRelated);
    }
    return this.ensureParentRefs(creative, api).then(() => {
      const afterHydrate = advertiserRefFromRelated(creative);
      if (afterHydrate) {
        return afterHydrate;
      }
      if (isOrderAdvertiserMismatchWarning(warning) || _.get(creative, ['lineItem', 'id'])) {
        return this.advertiserFromOrder(creative);
      }
      if (isFolioAdvertiserMismatchWarning(warning) || _.get(creative, ['creativeSet', 'id']) || _.get(creative, ['folio', 'id'])) {
        return this.advertiserFromFolio(creative);
      }
      return null;
    });
  }

  private ensureParentRefs(creative: any, api: CreativeApiService | LibraryCreativeApiService): Promise<void> {
    if (_.get(creative, ['lineItem', 'id']) || _.get(creative, ['creativeSet', 'id']) || _.get(creative, ['folio', 'id'])) {
      return Promise.resolve();
    }
    return firstValueFrom(api.get(creative.id)).then((fresh: any) => {
      if (_.get(fresh, ['lineItem', 'id'])) {
        creative.lineItem = creative.lineItem || fresh.lineItem;
      }
      if (_.get(fresh, ['creativeSet', 'id'])) {
        creative.creativeSet = creative.creativeSet || fresh.creativeSet;
      }
      if (_.get(fresh, ['folio', 'id'])) {
        creative.folio = creative.folio || fresh.folio;
      }
      if (!creative.validationWarnings && fresh.validationWarnings) {
        creative.validationWarnings = fresh.validationWarnings;
      }
    });
  }

  private advertiserFromOrder(creative: any): Promise<{id: string; name?: string} | null> {
    const lineItemId = _.get(creative, ['lineItem', 'id']);
    if (!lineItemId) {
      return Promise.resolve(null);
    }
    return firstValueFrom(this.lineItemApi.get(lineItemId)).then((lineItem: any) => {
      const fromLineItem = asAdvertiserRef(lineItem.advertiser) || asAdvertiserRef(_.get(lineItem, ['order', 'advertiser']));
      if (fromLineItem) {
        return fromLineItem;
      }
      const orderId = _.get(lineItem, ['order', 'id']);
      if (!orderId) {
        return null;
      }
      return firstValueFrom(this.orderApi.getDataView({id: orderId})).then((order: any) => {
        return asAdvertiserRef(order.advertiser);
      });
    });
  }

  private advertiserFromFolio(creative: any): Promise<{id: string; name?: string} | null> {
    const creativeSetId = _.get(creative, ['creativeSet', 'id']);
    const folioId = _.get(creative, ['folio', 'id']) || _.get(creative, ['creativeSet', 'folio', 'id']);
    const fromInfo = creativeSetId
      ? firstValueFrom(this.creativeSetInfoApi.get(creativeSetId)).then((cs) => {
        return asAdvertiserRef(cs.advertiser) || asAdvertiserRef(_.get(cs, ['folio', 'advertiser']));
      })
      : Promise.resolve(null);
    return fromInfo.then((advertiser) => {
      if (advertiser || !folioId) {
        return advertiser;
      }
      return firstValueFrom(this.folioApi.get(folioId)).then((folio: any) => {
        return asAdvertiserRef(folio.advertiser);
      });
    });
  }
}
